import type { Rng } from '../core/rng';
import type { OrderBias } from './orders';
import type {
  Accessory,
  ArchetypeId,
  CharacterLook,
  EyeStyle,
  FacialHair,
  HairStyle,
  HatStyle,
  ShirtStyle,
} from './types';

/* Everyone who walks through the door between 10 PM and 6 AM. */

export const SKIN = [
  '#f6d3b3',
  '#eab28a',
  '#d49a6a',
  '#b77b4f',
  '#8d5a36',
  '#6b4226',
  '#4f311e',
] as const;
export const HAIR = [
  '#24170f',
  '#3d2718',
  '#6b4125',
  '#a86b35',
  '#e2c275',
  '#8f8f8f',
  '#d8d8d8',
  '#a8412b',
] as const;
export const WILD_HAIR = ['#e05fb0', '#5fd3e0', '#9b6bff', '#7be07b'] as const;
export const SHIRTS = [
  '#c0392b',
  '#2e86de',
  '#27ae60',
  '#8e44ad',
  '#f39c12',
  '#16a085',
  '#34495e',
  '#d35400',
  '#7f8c8d',
  '#e84393',
] as const;

export interface Lines {
  arrive: readonly string[];
  happy: readonly string[];
  wrong: readonly string[];
  angry: readonly string[];
}

export interface Archetype {
  id: ArchetypeId;
  name: string;
  /** Shift hours [from, to) when they can randomly walk in. Empty = scripted only. */
  hours: readonly [number, number] | null;
  weight: number;
  patience: number;
  tip: number;
  seat: 'stool' | 'booth' | 'any';
  coffeeOnly: number;
  orderBias?: OrderBias;
  look: (rng: Rng) => CharacterLook;
  lines: Lines;
}

interface LookParts {
  skin?: string;
  hair?: string;
  hairStyle?: HairStyle;
  hat?: HatStyle;
  hatColor?: string;
  eyes?: EyeStyle;
  facialHair?: FacialHair;
  shirt?: string;
  shirtAlt?: string;
  shirtStyle?: ShirtStyle;
  accessory?: Accessory;
}

function baseLook(rng: Rng, parts: LookParts = {}): CharacterLook {
  return {
    species: 'human',
    skin: parts.skin ?? rng.pick(SKIN),
    hair: parts.hair ?? rng.pick(HAIR),
    hairStyle:
      parts.hairStyle ??
      rng.pick(['short', 'long', 'bun', 'curly', 'ponytail', 'buzz', 'afro'] as const),
    hat: parts.hat ?? 'none',
    hatColor: parts.hatColor ?? rng.pick(SHIRTS),
    eyes: parts.eyes ?? 'normal',
    facialHair: parts.facialHair ?? 'none',
    shirt: parts.shirt ?? rng.pick(SHIRTS),
    shirtAlt: parts.shirtAlt ?? rng.pick(SHIRTS),
    shirtStyle: parts.shirtStyle ?? 'plain',
    accessory: parts.accessory ?? 'none',
  };
}

const maybe = <T>(rng: Rng, p: number, value: T, otherwise: T): T =>
  rng.chance(p) ? value : otherwise;

export const GENERIC_WAITING = [
  '...',
  'Sweetheart?',
  '*checks watch*',
  'Any day now.',
  "Y'all forget me?",
  'I can hear my hash browns calling.',
  'Is it ready? Is it ready?',
  '*stares at the grill*',
] as const;

export const GENERIC_HAPPY = [
  'Perfect.',
  "That's the stuff.",
  'Scattered to perfection.',
  "*chef's kiss*",
  "Now THAT'S a Waffle House.",
  'Five stars. No notes.',
] as const;

export const GENERIC_WRONG = [
  'Close enough.',
  "I'll allow it.",
  "...it's 3AM, I'll eat it.",
] as const;

export const GENERIC_ANGRY = [
  "I'll just eat gas station sushi.",
  'Unbelievable.',
  '1 star. Would still come back.',
  "I'm going home to eat cereal.",
] as const;

/** The copypasta has no meaning. That's the point. */
export const NEW_HOST_LINE = 'The Waffle House has found its new host.';

export const ARCHETYPES: Record<ArchetypeId, Archetype> = {
  trucker: {
    id: 'trucker',
    name: 'Trucker',
    hours: [0, 8],
    weight: 10,
    patience: 1.3,
    tip: 1.3,
    seat: 'stool',
    coffeeOnly: 0.1,
    orderBias: { kinds: { bacon: 1.6, eggs: 1.4 } },
    look: (rng) =>
      baseLook(rng, {
        hat: 'trucker',
        hairStyle: rng.pick(['short', 'buzz', 'mullet'] as const),
        facialHair: rng.pick(['beard', 'mustache', 'stubble'] as const),
        shirtStyle: rng.pick(['flannel', 'plain'] as const),
      }),
    lines: {
      arrive: [
        "Evenin'. Coffee, black.",
        "Been drivin' eleven hours. Feed me.",
        "Same as always, darlin'.",
        "Load of chickens outside. Don't ask.",
      ],
      happy: ["That's the stuff.", "Y'all never miss.", 'Best meal in three states.'],
      wrong: ["...I'll allow it.", 'Close enough, chief.'],
      angry: ["Got a load to deliver. I'm out.", 'Truck stop it is.'],
    },
  },
  nurse: {
    id: 'nurse',
    name: 'Night Nurse',
    hours: [0, 8],
    weight: 7,
    patience: 1.05,
    tip: 1.2,
    seat: 'any',
    coffeeOnly: 0.05,
    look: (rng) =>
      baseLook(rng, {
        shirtStyle: 'scrubs',
        shirt: rng.pick(['#3fb5a3', '#5b8dd9', '#8e6bd1'] as const),
        accessory: 'lanyard',
        hairStyle: rng.pick(['bun', 'ponytail', 'short', 'curly'] as const),
      }),
    lines: {
      arrive: [
        'Twelve-hour shift. Feed me.',
        "Just got off. Don't talk to me yet.",
        "I've seen things tonight. Hash browns, please.",
      ],
      happy: ["You're a lifesaver. That's MY job.", 'Healing. Actual healing.'],
      wrong: ["I'm too tired to complain.", "...I've eaten worse in the break room."],
      angry: ['I have to be back in ten hours. Bye.'],
    },
  },
  college: {
    id: 'college',
    name: 'College Kid',
    hours: [0, 6],
    weight: 9,
    patience: 0.9,
    tip: 0.6,
    seat: 'any',
    coffeeOnly: 0.05,
    orderBias: { kinds: { waffle: 1.4 } },
    look: (rng) =>
      baseLook(rng, {
        shirtStyle: 'hoodie',
        hairStyle: rng.pick(['curly', 'long', 'short', 'afro', 'bun'] as const),
        eyes: maybe(rng, 0.3, 'glasses', 'sleepy'),
        hair: maybe(rng, 0.2, rng.pick(WILD_HAIR), rng.pick(HAIR)),
      }),
    lines: {
      arrive: [
        "Do y'all take Venmo?",
        'I have $4.37 and a dream.',
        'Finals in 5 hours. Perfect time for hash browns.',
        'Third time here today. Is that weird?',
      ],
      happy: [
        'BRO. This slaps.',
        'No cap, best meal of the semester.',
        'This is going on my story.',
      ],
      wrong: ["It's fine. I'll post it anyway.", "Mid. But I'm broke, so."],
      angry: ["I'm literally gonna tweet about this.", 'Ratio.'],
    },
  },
  club: {
    id: 'club',
    name: 'Club Crowd',
    hours: [3, 6],
    weight: 12,
    patience: 0.75,
    tip: 1.15,
    seat: 'any',
    coffeeOnly: 0,
    orderBias: { extraToppings: 1 },
    look: (rng) =>
      baseLook(rng, {
        shirtStyle: rng.pick(['sparkle', 'tank', 'plain'] as const),
        eyes: maybe(rng, 0.55, 'sunglasses', 'normal'),
        accessory: maybe(rng, 0.4, 'chain', 'none'),
        hair: maybe(rng, 0.25, rng.pick(WILD_HAIR), rng.pick(HAIR)),
      }),
    lines: {
      arrive: [
        'IS THIS THE AFTER PARTY',
        'I love this place. I LOVE this place.',
        'Where are my shoes. Whatever.',
        'WAFFLE HOUSE!!! WOOOO!',
      ],
      happy: ["YOU'RE MY BEST FRIEND", "I'd die for you. Seriously."],
      wrong: ["I can't even tell. It's perfect.", 'Tastes like... tastes like FOOD!'],
      angry: ['This is worse than the club.', "I'm gonna go cry in the Uber."],
    },
  },
  floridaMan: {
    id: 'floridaMan',
    name: 'Florida Man',
    hours: [2, 8],
    weight: 5,
    patience: 0.85,
    tip: 0.9,
    seat: 'stool',
    coffeeOnly: 0,
    orderBias: { extraToppings: 1, wellChance: 0.4 },
    look: (rng) =>
      baseLook(rng, {
        skin: '#f2a07b',
        hairStyle: 'mullet',
        hair: rng.pick(['#a86b35', '#e2c275', '#6b4125'] as const),
        shirtStyle: rng.pick(['tank', 'hawaiian'] as const),
        eyes: maybe(rng, 0.5, 'sunglasses', 'normal'),
        facialHair: rng.pick(['stubble', 'mustache', 'none'] as const),
      }),
    lines: {
      arrive: [
        "Don't ask about the shoes.",
        "I'm not allowed back at the Denny's.",
        "The gator's in the truck. He's fine.",
        'Long story. Hash browns first.',
      ],
      happy: ["Now THAT'S a Florida breakfast.", 'Tell the gator I said this was good.'],
      wrong: ["I've eaten worse. I've eaten a LOT worse.", 'Eh. Ate it anyway.'],
      angry: ["Fine. I'll go wrestle something.", "You'll hear from my lawyer, Gary."],
    },
  },
  cop: {
    id: 'cop',
    name: 'Off-Duty Cop',
    hours: [0, 8],
    weight: 4,
    patience: 1.4,
    tip: 1.0,
    seat: 'stool',
    coffeeOnly: 0.8,
    look: (rng) =>
      baseLook(rng, {
        shirt: '#243b63',
        shirtStyle: 'plain',
        accessory: 'badge',
        hairStyle: rng.pick(['buzz', 'short', 'bun'] as const),
        facialHair: maybe(rng, 0.4, 'mustache', 'none'),
      }),
    lines: {
      arrive: [
        "Just coffee. Don't mind me.",
        "I didn't see nothin'. Coffee, please.",
        "Quiet night, huh? ...Don't say it.",
      ],
      happy: ['Appreciate you.', 'Best coffee in the county.'],
      wrong: ["It's fine."],
      angry: ["...I'll get coffee at the station."],
    },
  },
  churchLady: {
    id: 'churchLady',
    name: 'Church Lady',
    hours: [7, 8],
    weight: 22,
    patience: 1.25,
    tip: 1.4,
    seat: 'any',
    coffeeOnly: 0.1,
    look: (rng) =>
      baseLook(rng, {
        hat: 'church',
        hatColor: rng.pick(['#e84393', '#9b59b6', '#f1c40f', '#1abc9c', '#e74c3c'] as const),
        hairStyle: rng.pick(['curly', 'bun', 'short'] as const),
        hair: rng.pick(['#8f8f8f', '#d8d8d8', '#24170f', '#3d2718'] as const),
        shirtStyle: 'plain',
        eyes: maybe(rng, 0.5, 'glasses', 'normal'),
      }),
    lines: {
      arrive: [
        'Good morning, sugar!',
        'We beat the after-church rush!',
        'Pray for this hash brown, Lord.',
      ],
      happy: ['Bless you, baby.', 'The Lord provides. And so do you!'],
      wrong: ['Well, bless your heart.', "We'll pray on it."],
      angry: ["We'll be praying for you, sugar.", 'Mm-hmm. MM. HMM.'],
    },
  },
  bachelorette: {
    id: 'bachelorette',
    name: 'Bachelorette Party',
    hours: [3, 6],
    weight: 5,
    patience: 0.85,
    tip: 1.5,
    seat: 'booth',
    coffeeOnly: 0,
    orderBias: { extraItems: 1, kinds: { waffle: 1.5 } },
    look: (rng) =>
      baseLook(rng, {
        hat: 'tiara',
        accessory: 'sash',
        shirt: rng.pick(['#e84393', '#f5f5f5', '#fd79a8'] as const),
        shirtStyle: 'sparkle',
        hairStyle: rng.pick(['long', 'ponytail', 'curly'] as const),
      }),
    lines: {
      arrive: [
        "SHE'S GETTING MARRIED!!!",
        'Hash browns for the BRIDE!',
        "We lost Ashley. She'll find us.",
      ],
      happy: ['THIS IS THE BEST NIGHT EVER', 'Brides eat free, right? Kidding. Unless?'],
      wrong: ["It's fine!! Everything is FINE!!", "We're too happy to care!!"],
      angry: ["Ugh, let's go to the OTHER after party."],
    },
  },
  groom: {
    id: 'groom',
    name: 'Guy in a Tux',
    hours: [2, 6],
    weight: 3,
    patience: 1.0,
    tip: 1.6,
    seat: 'stool',
    coffeeOnly: 0,
    orderBias: { kinds: { waffle: 1.5 } },
    look: (rng) =>
      baseLook(rng, {
        shirt: '#1e1e24',
        shirtAlt: '#f5f5f5',
        shirtStyle: 'suit',
        accessory: 'bowtie',
        hairStyle: rng.pick(['short', 'buzz', 'curly'] as const),
      }),
    lines: {
      arrive: [
        'I just got married. Like, an hour ago.',
        'My wife is in the car. She wants hash browns.',
        'Reception food was tiny. So tiny.',
      ],
      happy: ['Better than the wedding cake.', 'Best day of my life. This is part of it.'],
      wrong: ["It's my wedding night. I'm not complaining."],
      angry: ['My marriage has lasted longer than this wait.'],
    },
  },
  raccoon: {
    id: 'raccoon',
    name: 'Raccoon',
    hours: [5, 8],
    weight: 0.8,
    patience: 1.0,
    tip: 0.35,
    seat: 'stool',
    coffeeOnly: 0,
    orderBias: { extraItems: -1, kinds: { waffle: 0.2 } },
    look: (rng) => ({ ...baseLook(rng), species: 'raccoon', eyes: 'mask' }),
    lines: {
      arrive: ['*chitters politely*', '*places $3 on the counter*', '*washes hands in the coffee*'],
      happy: ['*happy raccoon noises*', '*tiny thumbs up*'],
      wrong: ['*confused chittering*'],
      angry: ['*knocks over the sugar and leaves*'],
    },
  },
  philosopher: {
    id: 'philosopher',
    name: 'Guy Explaining Crypto',
    hours: [2, 6],
    weight: 2.5,
    patience: 1.1,
    tip: 0.8,
    seat: 'stool',
    coffeeOnly: 0,
    look: (rng) =>
      baseLook(rng, {
        shirtStyle: 'turtleneck',
        shirt: '#1e1e24',
        eyes: 'glasses',
        hairStyle: rng.pick(['short', 'bun', 'long'] as const),
        facialHair: maybe(rng, 0.5, 'beard', 'none'),
      }),
    lines: {
      arrive: [
        'So basically, hash browns are a decentralized potato ledger, and—',
        "Okay but what if WE'RE the waffle, and the universe is the iron—",
        'You ever think the Roman Empire had a Waffle House? Like, spiritually—',
      ],
      happy: ['Bullish.', 'This hash brown is a store of value.'],
      wrong: ["Temporary dip. I'm holding."],
      angry: ["I'm going to short this establishment."],
    },
  },
  uniformGuy: {
    id: 'uniformGuy',
    name: "Guy In Uniform Who Doesn't Work Here",
    hours: [4, 7],
    weight: 1.5,
    patience: 1.2,
    tip: 1.0,
    seat: 'stool',
    coffeeOnly: 0,
    look: (rng) =>
      baseLook(rng, {
        shirt: '#f4c20d',
        shirtAlt: '#1e1e24',
        shirtStyle: 'polo',
        accessory: 'nametag',
        hat: 'visor',
        hatColor: '#1e1e24',
      }),
    lines: {
      arrive: [
        "I don't work here. I just like the shirt.",
        'People keep asking me for coffee. I just give it to them.',
        "I've been 'on break' since 2019.",
      ],
      happy: ["Great work. I'll tell corporate. I'm not corporate."],
      wrong: ["I'd have done it the same way, honestly."],
      angry: ['I quit! ...I never worked here.'],
    },
  },
  tiredParent: {
    id: 'tiredParent',
    name: 'Tired Parent',
    hours: [5, 8],
    weight: 3,
    patience: 1.0,
    tip: 1.3,
    seat: 'stool',
    coffeeOnly: 0.15,
    look: (rng) =>
      baseLook(rng, {
        accessory: 'baby',
        hairStyle: rng.pick(['bun', 'short', 'ponytail'] as const),
        eyes: 'sleepy',
      }),
    lines: {
      arrive: [
        "The baby's finally asleep. Please whisper-cook.",
        "Four AM. This is my 'me time'.",
        'Coffee. IV drip if you got it.',
      ],
      happy: ["You're the only adult who helped me today.", 'Oh my god. Hot food. HOT FOOD.'],
      wrong: ["I'll eat literally anything I don't have to cut up."],
      angry: ['Even the baby would have been faster.'],
    },
  },
  gymBro: {
    id: 'gymBro',
    name: 'Gym Bro',
    hours: [0, 8],
    weight: 4,
    patience: 0.9,
    tip: 1.0,
    seat: 'stool',
    coffeeOnly: 0,
    orderBias: { kinds: { eggs: 2, bacon: 2 }, extraItems: 1 },
    look: (rng) =>
      baseLook(rng, {
        shirtStyle: 'tank',
        hat: maybe(rng, 0.5, 'headband', 'cap'),
        hairStyle: rng.pick(['buzz', 'short'] as const),
      }),
    lines: {
      arrive: [
        'Double hash browns. Bulking season.',
        "Is there protein in the 'covered' part?",
        'Leg day tomorrow. Carbo-load me.',
      ],
      happy: ['GAINS.', 'Macros? Immaculate.'],
      wrong: ['Still gonna log it.'],
      angry: ['Bro. Bro. Not cool, bro.'],
    },
  },
  tourist: {
    id: 'tourist',
    name: 'Tourist From Ohio',
    hours: [0, 6],
    weight: 3,
    patience: 1.1,
    tip: 1.1,
    seat: 'any',
    coffeeOnly: 0,
    look: (rng) =>
      baseLook(rng, {
        hat: 'visor',
        hatColor: '#f5f5f5',
        shirtStyle: 'hawaiian',
        accessory: 'camera',
        eyes: maybe(rng, 0.4, 'sunglasses', 'normal'),
      }),
    lines: {
      arrive: [
        "Is this like a Denny's?",
        "So what does 'scattered' mean? And 'smothered'?",
        'My cousin said I HAD to come here.',
      ],
      happy: ["We don't have this in Ohio!", "I'm moving here."],
      wrong: ["I don't know what I expected, but okay!"],
      angry: ["I'm gonna go find a Bob Evans."],
    },
  },
  regular: {
    id: 'regular',
    name: 'Hank, The Regular',
    hours: null,
    weight: 0,
    patience: 1.5,
    tip: 1.8,
    seat: 'stool',
    coffeeOnly: 0,
    look: (rng) =>
      baseLook(rng, {
        skin: rng.pick(SKIN),
        hair: '#d8d8d8',
        hairStyle: 'short',
        hat: 'trucker',
        hatColor: '#2c3e50',
        facialHair: 'mustache',
        shirtStyle: 'flannel',
        shirt: '#8e2b2b',
        eyes: 'glasses',
      }),
    lines: {
      arrive: ['The usual.', 'Evening, Dottie. The usual.'],
      happy: ["Every night since '88. Never missed.", "That's how you do it."],
      wrong: ["...That ain't the usual, son."],
      angry: ['Forty years. FORTY YEARS.'],
    },
  },
  weatherman: {
    id: 'weatherman',
    name: 'The Weatherman',
    hours: null,
    weight: 0,
    patience: 1.3,
    tip: 1.4,
    seat: 'stool',
    coffeeOnly: 0,
    look: (rng) =>
      baseLook(rng, {
        hairStyle: 'bald',
        shirt: '#1f5fb8',
        shirtAlt: '#f4c20d',
        shirtStyle: 'hoodie',
        accessory: 'raincoat',
        skin: rng.pick(SKIN),
      }),
    lines: {
      arrive: [
        "Evening, folks. I'd get comfortable.",
        "Don't mind me. Just... checking in.",
        "If you've got somewhere to be, I'd go now.",
      ],
      happy: ["Best forecast I've had all night."],
      wrong: ["Scattered showers. Scattered hash browns. It's fine."],
      angry: ["I've been in Category 5s with better service."],
    },
  },
  fema: {
    id: 'fema',
    name: 'FEMA Agent',
    hours: null,
    weight: 0,
    patience: 2,
    tip: 1.5,
    seat: 'stool',
    coffeeOnly: 1,
    look: (rng) =>
      baseLook(rng, {
        shirt: '#1b2a4a',
        shirtStyle: 'plain',
        accessory: 'femaJacket',
        hat: 'cap',
        hatColor: '#1b2a4a',
        hairStyle: 'short',
      }),
    lines: {
      arrive: [
        '*writes something on a clipboard*',
        "Just observing. Pretend I'm not here.",
        "Coffee, please. It's for... research.",
      ],
      happy: ['*checks a box marked GREEN*'],
      wrong: ['*frowns at clipboard*'],
      angry: ["*writes 'RED' in large letters*"],
    },
  },
  streamer: {
    id: 'streamer',
    name: 'Streamer',
    hours: null,
    weight: 0,
    patience: 1.1,
    tip: 1.2,
    seat: 'stool',
    coffeeOnly: 0,
    orderBias: { extraToppings: 2 },
    look: (rng) =>
      baseLook(rng, {
        accessory: 'phone',
        hair: rng.pick(WILD_HAIR),
        hairStyle: rng.pick(['short', 'long', 'ponytail'] as const),
        shirtStyle: 'hoodie',
        hat: maybe(rng, 0.5, 'beanie', 'none'),
      }),
    lines: {
      arrive: [
        "CHAT, WE'RE LIVE AT THE WAFFLE HOUSE",
        "Chat says order it 'all the way'.",
        'Say hi to chat!',
      ],
      happy: ['CHAT IS GOING CRAZY', 'W cook. Massive W.'],
      wrong: ["Chat says it's mid. Chat is right."],
      angry: ["Chat, we're leaving. This is an L."],
    },
  },
  host: {
    id: 'host',
    name: '???',
    hours: null,
    weight: 0,
    patience: 1,
    tip: 1,
    seat: 'stool',
    coffeeOnly: 0,
    look: (rng) => ({
      ...baseLook(rng),
      species: 'host',
      eyes: 'glow',
      hat: 'hood',
      shirt: '#0b0b10',
      hatColor: '#0b0b10',
    }),
    lines: {
      arrive: ['...all the way...'],
      happy: ['...yes...'],
      wrong: ['...no...'],
      angry: ['...'],
    },
  },
  couple: {
    id: 'couple',
    name: 'The Couple',
    hours: null,
    weight: 0,
    patience: 1.4,
    tip: 1.5,
    seat: 'booth',
    coffeeOnly: 0,
    look: (rng) =>
      baseLook(rng, {
        shirtStyle: 'suit',
        shirt: '#2d3436',
        shirtAlt: '#f5f5f5',
        accessory: 'bowtie',
      }),
    lines: {
      arrive: ['*nervously pats pocket*', 'Table for two. Special occasion.'],
      happy: ['*gets down on one knee*'],
      wrong: ['*puts ring back in pocket*'],
      angry: ["We'll... do this somewhere else."],
    },
  },
  eggKaren: {
    id: 'eggKaren',
    name: 'Egg Surcharge Complainer',
    hours: null,
    weight: 0,
    patience: 0.7,
    tip: 0.5,
    seat: 'stool',
    coffeeOnly: 0,
    orderBias: { kinds: { eggs: 99 } },
    look: (rng) =>
      baseLook(rng, {
        hairStyle: 'bun',
        hair: '#e2c275',
        eyes: 'sunglasses',
        shirt: '#74b9ff',
        accessory: 'phone',
      }),
    lines: {
      arrive: [
        'Fifty cents?! For an EGG?!',
        "I'd like to speak to the egg manager.",
        "I remember when eggs were free. They weren't. But still.",
      ],
      happy: ['...Fine. It was worth fifty cents.'],
      wrong: ['And I PAID A SURCHARGE for this.'],
      angry: ["I'm raising my own chickens. Goodbye."],
    },
  },
  returning: {
    id: 'returning',
    name: 'Calmed Customer',
    hours: null,
    weight: 0,
    patience: 1.5,
    tip: 2.2,
    seat: 'stool',
    coffeeOnly: 0,
    look: (rng) => baseLook(rng),
    lines: {
      arrive: ['Remember me? ...Sorry about earlier.'],
      happy: [
        'Worth it. Every bit.',
        'You calmed me down AND fed me.',
        'I’m telling everybody about this place.',
      ],
      wrong: ['Still better than fighting.'],
      angry: ['...I’m calm. I’m calm. I’m leaving, but I’m calm.'],
    },
  },
  fighter: {
    id: 'fighter',
    name: 'Fighter',
    hours: null,
    weight: 0,
    patience: 1,
    tip: 0,
    seat: 'booth',
    coffeeOnly: 0,
    look: (rng) =>
      baseLook(rng, {
        shirtStyle: rng.pick(['tank', 'flannel', 'plain', 'hawaiian'] as const),
        hat: maybe(rng, 0.4, rng.pick(['trucker', 'cap', 'cowboy'] as const), 'none'),
        facialHair: maybe(rng, 0.4, 'beard', 'none'),
      }),
    lines: { arrive: [], happy: [], wrong: [], angry: [] },
  },
};

export const RANDOM_POOL: readonly Archetype[] = Object.values(ARCHETYPES).filter(
  (a) => a.hours !== null,
);

export function pickArchetype(
  rng: Rng,
  hour: number,
  exclude: ReadonlySet<ArchetypeId> = new Set(),
): Archetype {
  const pool = RANDOM_POOL.filter(
    (a) => a.hours && hour >= a.hours[0] && hour < a.hours[1] && !exclude.has(a.id),
  );
  const candidates = pool.length > 0 ? pool : [ARCHETYPES.trucker];
  return rng.weighted(candidates.map((a) => [a, a.weight] as const));
}

export const FIGHTER_NAMES = [
  'GUY IN JORTS',
  'UNCLE RANDY',
  'BRIDESMAID #3',
  'CAPTAIN FLIP-FLOPS',
  'MAN IN ONE (1) SHOE',
  'DUDE IN CAMO',
  'KAREN FROM BOOTH 2',
  'TIRED GRANDMA',
  'DAWGS FAN',
  'GATORS FAN',
  "GUY WHO SAID 'ACTUALLY'",
  "DJ LIL' HASHBROWN",
  'TWIN #1',
  'TWIN #2',
  'BIG MIKE',
  'LIL MIKE',
] as const;

export interface FightTopic {
  topic: string;
  lines: readonly [string, string, string];
}

export const FIGHT_TOPICS: readonly FightTopic[] = [
  {
    topic: 'GRITS: SUGAR OR SALT',
    lines: ['Grits need SUGAR.', 'Say that again. I dare you.', 'SUGAR.'],
  },
  {
    topic: 'WHO HAD THE BOOTH FIRST',
    lines: ["That's MY booth.", "I don't see your name on it.", 'Oh, you wanna see a name?'],
  },
  {
    topic: 'PEE-CAN VS PUH-KAHN',
    lines: ["It's PEE-can.", "It's puh-KAHN and you know it.", 'PEE. CAN.'],
  },
  {
    topic: 'IS A HOT DOG A SANDWICH',
    lines: ['A hot dog is a sandwich.', 'Take that back.', 'Make me.'],
  },
  {
    topic: 'THE LAST PECAN WAFFLE',
    lines: ["That last pecan waffle's mine.", 'Over my dead body.', 'That can be arranged.'],
  },
  {
    topic: 'WHOSE TURN ON THE JUKEBOX',
    lines: ['I paid for the jukebox!', 'Nobody wants to hear that song AGAIN.', 'It is a CLASSIC.'],
  },
  { topic: 'GEORGIA VS FLORIDA', lines: ['GO DAWGS!', 'Chomp on THIS.', "Y'all ain't won since—"] },
  {
    topic: 'WHO SAID "ACTUALLY"',
    lines: ['Well, ACTUALLY—', "Don't you 'actually' me.", 'Actually, I will.'],
  },
  {
    topic: 'WAFFLE HOUSE VS IHOP',
    lines: ["IHOP's pancakes are—", 'Choose your next words carefully.', '...fluffier.'],
  },
  {
    topic: 'WHO ORDERED FIRST',
    lines: ['I was here FIRST.', 'You were in the BATHROOM.', 'That counts!'],
  },
];

export const JUKEBOX_SONGS = [
  'Scattered, Smothered & Grooved',
  'There Are STILL Raisins In My Toast',
  '3AM Grill Blues',
  'Index Green (Anthem)',
] as const;
